import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Mail, User, AtSign, Shield } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { createEmail } from '../../../services/emailServiceSwitch';
import { useTeam } from '../../../context/TeamContext';
import './EmailManagement.css';

/**
 * AddEmailModal Component
 * 
 * Modal for adding new email recipients (triggers SES verification)
 */
export default function AddEmailModal({ isOpen, onClose, onSuccess }) {
  const { authorisedTeams, activeTeamId } = useTeam();
  const realTeams = authorisedTeams.filter(t => t.id !== null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    teamId: activeTeamId || ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Default the team to the active team each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, teamId: activeTeamId || '' }));
    }
  }, [isOpen, activeTeamId]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Team validation
    if (!formData.teamId) {
      newErrors.teamId = 'Select a team';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Call API to create email (triggers SES verification)
      await createEmail(formData);

      // Show success notification
      if (onSuccess) {
        onSuccess('Email added successfully. Verification email sent.');
      }

      // Reset form and close modal
      setFormData({ email: '', name: '', teamId: activeTeamId || '' });
      setErrors({});
      onClose();
    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to add email'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setFormData({ email: '', name: '', teamId: activeTeamId || '' });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Email Recipient"
      size="md"
      closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            icon={<Mail size={18} />}
          >
            Add Email
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="email-form">
        {/* Team Selector */}
        {realTeams.length > 1 && (
          <div className="form-group">
            <label htmlFor="teamId" className="form-label">
              <Shield size={16} />
              Team
            </label>
            <select id="teamId" name="teamId" value={formData.teamId} onChange={handleChange} className={`form-select ${errors.teamId ? 'error' : ''}`} disabled={loading}>
              <option value="">Select a team</option>
              {realTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {errors.teamId && <span className="error-text">{errors.teamId}</span>}
          </div>
        )}

        {/* Email Field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            <AtSign size={16} />
            Email Address
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john.doe@example.com"
            error={errors.email}
            disabled={loading}
          />
          {errors.email && (
            <span className="error-text">{errors.email}</span>
          )}
          <p className="field-hint">
            A verification email will be sent to this address
          </p>
        </div>

        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            <User size={16} />
            Contact Name
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            error={errors.name}
            disabled={loading}
          />
          {errors.name && (
            <span className="error-text">{errors.name}</span>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="form-error">
            {errors.submit}
          </div>
        )}
      </form>
    </Modal>
  );
}

AddEmailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
